import React, { useEffect, useState, useReducer } from 'react';
import PropTypes from 'prop-types';
import {
  SelectNative,
  SelectNativeOption,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  InfoTooltipButton,
  ComponentLevelNotification,
  Label,
} from '@americanexpress/dls-react';
import { validateDlsInput } from '../../Validations/Validations';
import { parseHtml, getClassNameWithoutHorizontalPadding } from '../../utils/util';
import { performOperations } from '../../Operations/Operations';
import useDropDownOptions from '../../CustomHooks/useDropDownOptions';

const getVal = require('lodash/get');
/**
 * returns DLS select component
 * @param {*} props
 */
/* eslint complexity: ["error", 40] -- need to increase complexity */
/* eslint-disable max-len -- need */
const DlsSelect = (props) => {
  const {
    fetchUrl,
    fieldLabel,
    fieldId,
    fieldValidation,
    cssProperties,
    selectKey,
    selectShowKey,
    memberInfo,
    onElementChange = [],
    handleOnChangeEvents,
    showProductInfo,
    fieldDefaultOptionLabel = 'Choose One',
    showValueAsCardNumber = false,
    filledFormData,
    formSubmissionPayload = filledFormData,
    setFormSubmissionPayload,
    validations = [],
    setValidationErrors,
    validationErrors,
    setFocus,
    fieldRank,
    idsToClear,
    entityToEdit,
    dlsSelectKey,
    preFilledWith,
    isPreFilled,
    setShowPopUp,
    popUpTriggerMappings = {},
    curPageValues,
    setCurPageValues,
    comments,
    setComments,
    dispatchIdsState,
    fieldIdsToClear = [],
    operationsToPerform,
    changePageValues,
    setChangePageValues,
    isChangePageValue,
    fieldDynamicRequired = null,
    dataObject,
    resetOnPayloadClear = false,
    loggerApiCall,
    objectOfFunctions,
    prefillStyleConfig,
    setEntityData,
    setIsFormSubmission,
    entityComments = [],
    entityCompositeKey,
    showId,
  } = props;
  const [expandableTableData, setExpandableTableData] = useState({});
  const [isUserEdited, setIsUserEdited] = useState(false);
  const options = useDropDownOptions({
    ...props,
    dataObject: {
      formSubmissionPayload,
      memberInfo,
      expandableTableData,
      setExpandableTableData,
    },
  });
  const [showCustomInvalidMsg, setShowCustomInvalidMsg] = useState(false);
  const [status, setStatus] = useState('default');
  const customInvalidMsg = fieldValidation && fieldValidation.customInvalidMsg
    ? fieldValidation.customInvalidMsg
    : 'Please select an item in the list';
  const [value, setValue] = useState('');
  const [validationErrState, dispatchErrState] = useReducer(
    (prevState, payload) => ({ ...prevState, ...payload }),
    { validationErr: '', isValid: true }
  );
  const optionalLabel = fieldLabel && fieldLabel.optionalLabel
    ? fieldLabel.optionalLabel
    : ' (Optional)';
  const payloadFieldValue = filledFormData ? filledFormData[fieldId] : undefined;
  const updatePayloadWithValue = (val) => {
    if (onElementChange && onElementChange.length > 0) {
      onElementChange.forEach((changeEvent) => {
        changeEvent.functionsToCall.forEach((fn) => {
          switch (fn.name) {
            case 'setFormSubmissionPayload':
              if (val) {
                filledFormData[fieldId] = val;
                setFormSubmissionPayload({
                  ...filledFormData,
                  elemId: fieldId,
                });
              } else {
                delete filledFormData[fieldId];
                setFormSubmissionPayload({
                  ...filledFormData,
                  elemId: fieldId,
                });
              }
              break;
            default:
              return true;
          }
          return true;
        });
        return true;
      });
    }
  };

  const performCustomValidations = (params) => {
    const { inputVal, validationsToPerform } = params;
    const invalidMsg = validateDlsInput(inputVal, validationsToPerform, {
      ...dataObject,
      isFieldRequired: fieldValidation.required,
    });
    if (invalidMsg) {
      dispatchErrState({ validationErr: `${invalidMsg}`, isValid: false });
    } else {
      dispatchErrState({ validationErr: '', isValid: true });
    }
  };

  useEffect(() => {
    if (fieldDynamicRequired && fieldValidation && !fieldValidation.required) {
      const mandatoryValidation = validations.find(
        (val) => val.type === 'mandatoryField'
      );
      if (mandatoryValidation) {
        performCustomValidations({
          inputVal: value,
          validationsToPerform: validations,
        });
      }
    }
  }, [fieldValidation.required]);

  /**
   * Handle dropdown selection
   */
  const handleSelect = (e, val = null) => {
    if (fieldIdsToClear && fieldIdsToClear.length > 0) {
      const clearIds = {};
      fieldIdsToClear.forEach((id, i) => {
        clearIds[id] = i + 1;
      });
      dispatchIdsState({ toggleState: !idsToClear.toggleState, ids: clearIds });
    }

    if (comments && comments[fieldId]) {
      delete comments[fieldId];
      setComments({ ...comments });
    }

    if (e && status === 'error') {
      setStatus('default');
    }
    const selectedVal = e ? e.target.value : val;
    if (e) setIsUserEdited(true);
    if (validations.length > 0) {
      performCustomValidations({
        inputVal: selectedVal,
        validationsToPerform: validations,
      });
    }
    if (operationsToPerform?.onChange?.length > 0) {
      performOperations({
        value: selectedVal,
        operations: operationsToPerform.onChange,
        dataObject: {
          ...dataObject,
          memberInfo,
          formSubmissionPayload,
          curPageValues,
          expandableTableData,
          setExpandableTableData,
          changePageValues,
          setChangePageValues,
          selectedVal,
          e,
        },
        functionObject: {
          setFormSubmissionPayload,
          loggerApiCall,
          setEntityData,
          setIsFormSubmission,
        },
        setFormSubmissionPayload,
        fieldId,
        e,
      });
    }
    if (e && onElementChange?.length > 0) {
      handleOnChangeEvents(onElementChange, e);
    }
    if (popUpTriggerMappings && popUpTriggerMappings[selectedVal]) {
      setShowPopUp(popUpTriggerMappings[selectedVal]);
    }
    setCurPageValues({ ...curPageValues, [fieldId]: selectedVal });
    setValue(selectedVal);

    if (isChangePageValue) {
      changePageValues[fieldId] = selectedVal;
      setChangePageValues({ ...changePageValues });
    }
  };

  useEffect(() => {
    if (isChangePageValue && fieldId in changePageValues) {
      setValue(getVal(changePageValues, fieldId));
    }
    if (!validationErrState.isValid) {
      performCustomValidations({
        inputVal: getVal(changePageValues, fieldId),
        validationsToPerform: validations,
      });
    }
  }, [changePageValues, isChangePageValue]);
  /**
   * Clear the values from previous select components
  */

  useEffect(() => {
    if (filledFormData && (filledFormData[fieldId] || filledFormData[fieldId] === '')) {
      setValue(filledFormData[fieldId]);
      curPageValues[fieldId] = filledFormData[fieldId];
      setCurPageValues({ ...curPageValues });
      if (isChangePageValue) {
        changePageValues[fieldId] = filledFormData[fieldId];
        setChangePageValues({ ...changePageValues });
      }
    }
  }, [fieldId]);

  useEffect(() => {
    if (!resetOnPayloadClear) return;
    const hasFieldKey = filledFormData && Object.prototype.hasOwnProperty.call(filledFormData, fieldId);
    const fieldValue = hasFieldKey ? filledFormData[fieldId] : undefined;
    if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
      setValue('');
    }
  }, [filledFormData, fieldId, resetOnPayloadClear]);

  useEffect(() => {
    // Keep the select UI in sync when payload is updated externally (for example by button keysToUpdate).
    if (payloadFieldValue === '') {
      setValue('');
    }
  }, [payloadFieldValue]);

  useEffect(() => {
    // Sync only non-empty external payload updates without altering existing blank/reset behavior.
    if (payloadFieldValue === undefined || payloadFieldValue === null || payloadFieldValue === '') {
      return;
    }

    if (payloadFieldValue !== value) {
      setValue(payloadFieldValue);
    }
  }, [payloadFieldValue, value]);

  // need to take out code of fieldId change from here
  useEffect(() => {
    if (isPreFilled && preFilledWith === 'filledFormData' && !fetchUrl) {
      if (filledFormData) {
        setValue(getVal(filledFormData, dlsSelectKey));
      }
    } else if (isChangePageValue && fieldId in changePageValues) {
      setValue(getVal(changePageValues, fieldId));
    }
  }, [filledFormData, fieldId]);

  useEffect(() => {
    if (validationErrState.isValid && validationErrors[fieldRank]) {
      delete validationErrors[fieldRank];
      setValidationErrors(validationErrors);
    } else if (!validationErrState.isValid) {
      setValidationErrors({
        ...validationErrors,
        ...{ [fieldRank]: `${fieldId}` },
      });
    }
  }, [validationErrState.isValid]);

  useEffect(() => {
    if (entityToEdit && entityToEdit[fieldId]) {
      handleSelect(null, entityToEdit[fieldId]);
      updatePayloadWithValue(entityToEdit[fieldId]);
      if (isChangePageValue) {
        changePageValues[fieldId] = entityToEdit[fieldId];
        setChangePageValues({ ...changePageValues });
      }
    }
  }, [entityToEdit]);

  useEffect(() => {
    const matchedEntity = entityComments.find(
      (obj) => obj.compositeKey === entityCompositeKey
        && (!showId || !obj.showId || obj.showId === showId)
    );
    if (matchedEntity && fieldId in matchedEntity) {
      setStatus('error');
    }
  }, [entityComments, entityCompositeKey]);

  useEffect(() => {
    if (getVal(idsToClear.ids, fieldId)) {
      updatePayloadWithValue(null);
      setValue('');
      changePageValues[fieldId] = '';
      setChangePageValues({ ...changePageValues });
      delete curPageValues[fieldId];
      setCurPageValues({ ...curPageValues });
      delete filledFormData[fieldId];
      setFormSubmissionPayload({ ...filledFormData });
      // should we delete this fieldID from filledFormData as well?
      delete idsToClear.ids[fieldId];
      dispatchIdsState({
        toggleState: idsToClear.toggleState,
        ids: idsToClear,
      });
    }
  }, [idsToClear.toggleState]);

  // Prefill style: resolved from formSubmissionPayload via matchAPIKey (full dotted path).
  // Row-level fields use {index} placeholder in matchAPIKey, replaced by entityToEdit.__rawApiIndex.
  let showPrefillStyle = false;
  if (prefillStyleConfig) {
    /* eslint-disable-next-line no-underscore-dangle -- internal marker set during API prefill */
    const rawApiIndex = entityToEdit?.__rawApiIndex ?? 0;
    const resolvedKey = prefillStyleConfig.matchAPIKey?.replace('{index}', rawApiIndex);
    const apiValue = getVal(formSubmissionPayload, resolvedKey);
    const currentValueSource = getVal(objectOfFunctions, prefillStyleConfig.sourcePath);
    const currentValue = getVal(currentValueSource, fieldId);
    const hasPrefillSource = apiValue !== undefined && apiValue !== null && apiValue !== ''
      && apiValue === currentValue;
    showPrefillStyle = hasPrefillSource && !isUserEdited;
  }
  const prefillInlineStyle = showPrefillStyle ? prefillStyleConfig.style || {} : {};

  const invalidHandler = (e) => {
    if (validations.length > 0) {
      e.preventDefault();
      handleSelect(e);
      setFocus({ ...validationErrors, ...{ [fieldRank]: `${fieldId}` } });
    } else if (fieldValidation && fieldValidation.hideDefaultValidationMsg) {
      e.preventDefault();
      document.querySelector(`#${fieldId}`)?.focus();
      setShowCustomInvalidMsg(true);
      setStatus('error');
    } else {
      e.target.setCustomValidity(`${customInvalidMsg}`);
    }
  };

  const onInputHandler = (e) => {
    e.target.setCustomValidity('');
    setShowCustomInvalidMsg(false);
    setStatus('default');
  };

  const showErrorMessage = (errorMessage) => (
    <ComponentLevelNotification id={`${fieldId}-errorMessage`} className="pad-1-t">
      {parseHtml(errorMessage)}
    </ComponentLevelNotification>
  );

  const selectLabel = (
    <>
      {fieldLabel && fieldLabel.label && (
        <Label
          id={`${fieldId}-label`}
          htmlFor={fieldId}
          className={fieldLabel.cssProperties.style}
        >
          {parseHtml(fieldLabel.label)}
          <span> </span>
          {!fieldValidation.required && <span>{optionalLabel}</span>}
        </Label>
      )}
      {fieldValidation && fieldValidation.isToolTip && (
        <>
          <span> </span>
          <Tooltip id={`${fieldId}-tooltip`}>
            <TooltipTrigger>
              <InfoTooltipButton screenReaderLabel="info" />
            </TooltipTrigger>
            <TooltipContent>{fieldValidation.toolTipInfo}</TooltipContent>
          </Tooltip>
        </>
      )}
      {fieldLabel && fieldLabel.subLabel && (
        <div>
          <Label
            htmlFor={fieldId}
            className={fieldLabel.cssProperties.subLabelStyle}
          >
            {parseHtml(fieldLabel.subLabel)}
          </Label>
        </div>
      )}
    </>
  );

  /* eslint-disable unicorn/no-nested-ternary -- required T */
  return (
    <div className={getClassNameWithoutHorizontalPadding(cssProperties.style)} data-toggle="select">
      <SelectNative
        label={selectLabel}
        status={status}
        name={fieldId}
        id={fieldId}
        aria-labelledby={`${fieldId}-label`}
        value={value}
        onChange={handleSelect}
        required={fieldValidation.required}
        aria-disabled={fieldValidation.disabled}
        title=""
        onInvalid={invalidHandler}
        onInput={onInputHandler}
        style={prefillInlineStyle}
      >
        {!selectKey && !selectShowKey && Array.isArray(options)
          ? options.map((option) => (
            <SelectNativeOption key={option.value} value={option.value}>
              {option.label}
            </SelectNativeOption>
          ))
          : (
            <>
              <SelectNativeOption value="">
                {fieldDefaultOptionLabel}
              </SelectNativeOption>
              {!showProductInfo && options && options.length > 0
                ? options.map((optionObj) => (
                  <SelectNativeOption
                    key={
                      optionObj[selectKey].accountKey
                        ? optionObj[selectKey].accountKey
                        : optionObj.accountToken
                    }
                    value={
                      optionObj[selectKey].accountKey
                        ? optionObj[selectKey].accountKey
                        : optionObj.accountToken
                    }
                  >
                    {optionObj[selectKey][selectShowKey]}
                  </SelectNativeOption>
                ))
                : !showValueAsCardNumber && options && options.length > 0
                  ? options.map((optionObj) => (
                    <SelectNativeOption
                      key={
                        optionObj[selectKey].accountKey
                          ? optionObj[selectKey].accountKey
                          : optionObj.accountToken
                      }
                      value={
                        optionObj[selectKey].accountKey
                          ? optionObj[selectKey].accountKey
                          : optionObj.accountToken
                      }
                    >
                      {getVal(optionObj, 'product.digitalInfo.productDesc')
                        ? optionObj.product.digitalInfo.productDesc
                        : ''}{' '}
                      (-{optionObj[selectKey][selectShowKey].split('-')[2]})
                    </SelectNativeOption>
                  ))
                  : options
                  && options.length > 0
                  && options.map((optionObj) => (
                    <SelectNativeOption
                      key={optionObj[selectKey][selectShowKey]}
                      value={optionObj[selectKey][selectShowKey]}
                    >
                      {getVal(optionObj, 'product.digitalInfo.productDesc')
                        ? optionObj.product.digitalInfo.productDesc
                        : ''}{' '}
                      (-{optionObj[selectKey][selectShowKey].split('-')[2]})
                    </SelectNativeOption>
                  ))}
            </>
          )}
      </SelectNative>
      {/* eslint-enable unicorn/no-nested-ternary -- required T */}
      {fieldValidation.disabled && (
        <input type="hidden" id={`${fieldId}-hidden`} name={fieldId} value={value} />
      )}
      {showCustomInvalidMsg && showErrorMessage(customInvalidMsg)}
      {validationErrState.validationErr
        && showErrorMessage(validationErrState.validationErr)}
      {comments && comments[fieldId]
        && showErrorMessage(comments[fieldId].comment)}
      {(() => {
        const matchedEntity = entityComments.find(
          (obj) => obj.compositeKey === entityCompositeKey
            && (!showId || !obj.showId || obj.showId === showId)
        );
        return status === 'error' && matchedEntity && fieldId in matchedEntity
          && showErrorMessage(matchedEntity[fieldId]?.defaultValue || matchedEntity[fieldId]);
      })()}
    </div>
  );
};
/* eslint-enable max-len -- need */
DlsSelect.propTypes = {
  fetchUrl: PropTypes.string,
  fieldLabel: PropTypes.objectOf(PropTypes.shape),
  fieldId: PropTypes.string,
  fieldValidation: PropTypes.objectOf(PropTypes.shape),
  cssProperties: PropTypes.objectOf(PropTypes.shape),
  selectKey: PropTypes.string,
  selectShowKey: PropTypes.string,
  onElementChange: PropTypes.arrayOf(PropTypes.shape),
  handleOnChangeEvents: PropTypes.func,
  showProductInfo: PropTypes.bool,
  fieldDefaultOptionLabel: PropTypes.string,
  showValueAsCardNumber: PropTypes.bool,
  filledFormData: PropTypes.objectOf(PropTypes.shape),
  formSubmissionPayload: PropTypes.objectOf(PropTypes.shape),
  setValidationErrors: PropTypes.func,
  validationErrors: PropTypes.objectOf(PropTypes.shape),
  validations: PropTypes.arrayOf(PropTypes.shape),
  setFocus: PropTypes.func,
  fieldRank: PropTypes.number,
  setFormSubmissionPayload: PropTypes.func,
  idsToClear: PropTypes.objectOf(PropTypes.shape),
  entityToEdit: PropTypes.objectOf(PropTypes.shape),
  dlsSelectKey: PropTypes.string,
  isPreFilled: PropTypes.bool,
  preFilledWith: PropTypes.string,
  setShowPopUp: PropTypes.func,
  popUpTriggerMappings: PropTypes.objectOf(PropTypes.shape),
  curPageValues: PropTypes.objectOf(PropTypes.shape),
  setCurPageValues: PropTypes.func,
  comments: PropTypes.objectOf(PropTypes.shape),
  setComments: PropTypes.func,
  memberInfo: PropTypes.objectOf(PropTypes.shape),
  dispatchIdsState: PropTypes.func,
  fieldIdsToClear: PropTypes.arrayOf(PropTypes.string),
  operationsToPerform: PropTypes.objectOf(PropTypes.shape),
  changePageValues: PropTypes.objectOf(PropTypes.shape),
  setChangePageValues: PropTypes.func,
  isChangePageValue: PropTypes.bool,
  fieldDynamicRequired: PropTypes.objectOf(PropTypes.shape),
  dataObject: PropTypes.objectOf(PropTypes.shape),
  // eslint-disable-next-line react/boolean-prop-naming -- existing prop name used by callers, cannot rename
  resetOnPayloadClear: PropTypes.bool,
  loggerApiCall: PropTypes.func,
  setEntityData: PropTypes.func,
  objectOfFunctions: PropTypes.objectOf(PropTypes.shape),
  setIsFormSubmission: PropTypes.func,
  prefillStyleConfig: PropTypes.shape({
    sourcePath: PropTypes.string,
    matchAPIKey: PropTypes.string,
    style: PropTypes.objectOf(PropTypes.string),
    className: PropTypes.string,
  }),
  entityComments: PropTypes.arrayOf(PropTypes.shape),
  entityCompositeKey: PropTypes.string,
  showId: PropTypes.string,
};

export default DlsSelect;
